import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ShieldCheck, ExternalLink } from "lucide-react";

export default function AdminKYC() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [viewing, setViewing] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  const load = () =>
    api
      .get(`/admin/kyc${filter === "all" ? "" : `?status=${filter}`}`)
      .then(({ data }) => setList(data))
      .catch(() => {});

  useEffect(() => {
    load();
  }, [filter]);

  const approve = async (userId) => {
    try {
      await api.post(`/admin/kyc/${userId}/approve`, {});
      toast.success("Approved");
      setViewing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const reject = async () => {
    if (!reason.trim()) {
      toast.error("Provide a reason");
      return;
    }
    try {
      await api.post(`/admin/kyc/${rejecting.user_id}/reject`, { reason });
      toast.success("Rejected");
      setRejecting(null);
      setReason("");
      setViewing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <div className="overline">Compliance</div>
      <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-white mt-2 mb-6">
        KYC reviews
      </h1>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="pending" data-testid="kyc-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="kyc-tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="kyc-tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all" data-testid="kyc-tab-all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="mt-6 space-y-3">
        {list.length === 0 ? (
          <div className="bp-surface rounded-2xl p-12 text-center text-gray-500">
            No submissions in this view
          </div>
        ) : (
          list.map((s) => (
            <div key={s.id} className="bp-surface rounded-xl p-4 flex flex-wrap items-center gap-4" data-testid={`kyc-row-${s.id}`}>
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="text-white">{s.full_name}</div>
                <div className="text-xs text-gray-400">{s.user_email}</div>
              </div>
              <div className="text-xs text-gray-500">
                <div className="font-mono text-white">{s.country}</div>
                <div>{s.id_type?.replace("_", " ")}</div>
              </div>
              <StatusBadge status={s.status} />
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => setViewing(s)} data-testid={`kyc-review-${s.id}`}>
                Review
              </Button>
            </div>
          ))
        )}
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="bg-[#0B1120] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Identity review</DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-5">
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                <Info label="Full name" value={viewing.full_name} />
                <Info label="DOB" value={viewing.date_of_birth} />
                <Info label="Country" value={viewing.country} />
                <Info label="ID type" value={viewing.id_type?.replace("_", " ")} />
                <div className="md:col-span-2">
                  <Info label="Address" value={viewing.address} />
                </div>
                <Info label="Submitted" value={new Date(viewing.submitted_at).toLocaleString()} />
                <Info label="Status" value={viewing.status} />
              </div>

              {(viewing.nok_full_name || viewing.nok_phone) && (
                <div className="pt-4 border-t border-white/5">
                  <div className="overline mb-3">Next of kin</div>
                  <div className="grid md:grid-cols-2 gap-3 text-sm">
                    <Info label="Name" value={viewing.nok_full_name} />
                    <Info label="Relationship" value={viewing.nok_relationship} />
                    <Info label="Phone" value={viewing.nok_phone} />
                    <Info label="Email" value={viewing.nok_email} />
                    <div className="md:col-span-2">
                      <Info label="Address" value={viewing.nok_address} />
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <DocPreview label="ID front" url={viewing.id_front_url} />
                <DocPreview label="ID back" url={viewing.id_back_url} />
                <DocPreview label="Selfie" url={viewing.selfie_url} />
                <DocPreview label="Proof of address" url={viewing.proof_of_address_url} />
              </div>

              {viewing.rejection_reason && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-300">
                  Previous rejection: {viewing.rejection_reason}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            {viewing?.status === "pending" && (
              <>
                <Button variant="outline" className="rounded-full text-red-400 border-red-500/40 hover:bg-red-500/10" onClick={() => setRejecting(viewing)} data-testid="kyc-reject-btn">
                  <XCircle className="w-4 h-4 mr-1" /> Reject
                </Button>
                <Button className="btn-gold rounded-full" onClick={() => approve(viewing.user_id)} data-testid="kyc-approve-btn">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejecting} onOpenChange={(o) => !o && (setRejecting(null), setReason(""))}>
        <DialogContent className="bg-[#0B1120] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading">Rejection reason</DialogTitle>
          </DialogHeader>
          <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Selfie is blurry, please resubmit a clearer photo." className="bg-[#050914] border-white/10" data-testid="kyc-reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejecting(null); setReason(""); }}>Cancel</Button>
            <Button className="bg-red-500 hover:bg-red-600 text-white rounded-full" onClick={reject} data-testid="kyc-confirm-reject">
              Confirm rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="overline text-[10px]">{label}</div>
      <div className="text-white mt-1">{value || "—"}</div>
    </div>
  );
}

function DocPreview({ label, url }) {
  if (!url) return (
    <div>
      <div className="overline text-[10px] mb-2">{label}</div>
      <div className="aspect-square rounded-lg bg-white/5 border border-dashed border-white/10 flex items-center justify-center text-[11px] text-gray-500">
        Not provided
      </div>
    </div>
  );
  return (
    <div>
      <div className="overline text-[10px] mb-2 flex items-center gap-1">
        {label}
        <a href={url} target="_blank" rel="noreferrer" className="text-[#D4AF37] hover:underline">
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      <a href={url} target="_blank" rel="noreferrer">
        <img src={url} alt={label} className="aspect-square rounded-lg object-cover border border-white/10" />
      </a>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { c: "#F59E0B", bg: "#F59E0B15" },
    approved: { c: "#10B981", bg: "#10B98115" },
    rejected: { c: "#EF4444", bg: "#EF444415" },
  };
  const s = map[status] || map.pending;
  return (
    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full" style={{ color: s.c, background: s.bg, border: `1px solid ${s.c}40` }}>
      {status}
    </span>
  );
}
